<script lang="ts" setup>
import { ButtonSetProps, ButtonSetValueType } from '@/helper/Types'
import { computed, onMounted, ref } from 'vue'

/**
 * Component building a button row or button set from the given button data in array
 * The buttons are selectable buttons which function like radio or checkbox inputs and get marked if got selected
 * Can function as single select or multi select
 */
const props = withDefaults(
  defineProps<{
    modelValue?: string | string[] | boolean | undefined
    label?: string
    required?: boolean
    name: string
    checkbox?: boolean
    errorText?: string
    setLabelCaption?: string
    hasLargeSpace?: boolean // Defines a bigger margin below
    fillWidth?: boolean // Buttons stretch on the full available width, false: btn width is 25%
    autoBtnWidth?: boolean // Buttons have an auto width (no grid)
    large?: boolean // Buttons are large squares (with icon in middle)
    svgIcon?: boolean //Icon as SVG
    isUnselectable?: boolean //Enable unselect of the selection
    buttons: ButtonSetProps[] // Contains all the buttons with their relevant data
  }>(),
  {
    label: undefined,
    errorText: undefined,
    setLabelCaption: undefined,
    modelValue: undefined
  }
)

const selectedValue = ref<string | string[] | boolean | undefined>('')

onMounted((): void => {
  selectedValue.value = props.modelValue
})

function getButtonId(val: string | boolean | number, index: number): string {
  return 'select-btn-' + props.name + '-' + val.toString().toLowerCase() + '-' + index
}

const isMultiselect = computed<boolean>(() => Array.isArray(props.modelValue))
const inputType = computed<string>(() => (isMultiselect.value ? 'checkbox' : 'radio'))

function btnIsChecked(btnValue: ButtonSetValueType): boolean {
  return Array.isArray(props.modelValue)
    ? props.modelValue.some(val => val.toString() == btnValue.toString())
    : props.modelValue == btnValue
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | string[] | boolean): void
  (e: 'change', value: string | string[] | boolean): void
}>()

/**
 * For deselect functionality
 */
function labelClicked(e: Event, val: ButtonSetValueType) {
  if (
    isMultiselect.value &&
    !props.isUnselectable &&
    (props.modelValue as string[]).length == 1 &&
    (props.modelValue as string[])[0] == val
  ) {
    e.preventDefault()
    return
  }
  if (isMultiselect.value || props.modelValue != val || !props.isUnselectable) {
    return
  }

  // run if single select (radio) and the same value clicked as actually selected, so a deselect is made possible
  e.preventDefault()
  emit('update:modelValue', '')
  selectedValue.value = ''
}

function inputChanged(e: Event): void {
  const input = e.currentTarget as HTMLInputElement
  const inputValue = typeof props.modelValue === 'boolean' ? input.dataset.value == 'true' : input.dataset.value
  const inputValueStr = (inputValue || '').toString()
  if (Array.isArray(selectedValue.value)) {
    if (input.checked) {
      selectedValue.value.push(inputValueStr)
    } else {
      selectedValue.value.splice(selectedValue.value.indexOf(inputValueStr), 1)
    }
  } else {
    selectedValue.value = typeof props.modelValue === 'boolean' ? !!inputValue : inputValue || ''
  }
  emit('update:modelValue', selectedValue.value)
  emit('change', selectedValue.value)
}
</script>

<template>
  <div
    :id="name"
    :class="{
      'has--large-space': hasLargeSpace,
      'is--required': required,
      'is--small': !large,
      'is--large': svgIcon
    }"
    class="select-btn__row form__row"
  >
    <p v-if="label" class="select-btn__description form__description">
      {{ label }}
    </p>
    <div class="form__element">
      <div v-if="setLabelCaption" class="col-12 select-btn__name">
        <label>{{ setLabelCaption }}</label>
      </div>
      <div class="row-wrapper">
        <div :class="{ 'is--auto': autoBtnWidth }" class="row g-4">
          <div
            v-for="(btn, i) in buttons"
            :key="'select-btn-' + name + i"
            :class="[
              fillWidth ? 'col-md-' + Math.ceil(12 / buttons.length) : autoBtnWidth ? 'col-md-auto' : 'col-md-3',
              { 'is--multiselect': isMultiselect }
            ]"
            class="select-btn"
          >
            <input
              :id="getButtonId(btn.value, i)"
              :checked="btnIsChecked(btn.value)"
              :data-value="btn.value.toString()"
              :disabled="btn.disabled ? btn.disabled() : false"
              :name="name"
              :type="inputType"
              class="select-btn__input"
              @change="inputChanged($event)"
            >
            <label
              v-if="isMultiselect"
              :for="getButtonId(btn.value, i)"
              class="form__cm select-btn__cm"
              @click="labelClicked($event, btn.value)"
            />
            <label
              :class="{ 'is--small': !large, 'is--large': svgIcon }"
              :data-label="getButtonId(btn.value, i)"
              :for="getButtonId(btn.value, i)"
              class="select-btn__label"
              @click="labelClicked($event, btn.value)"
            >
              <svg v-if="svgIcon" class="select-btn__svg" viewBox="0 0 100 90">
                <use :xlink:href="btn.iconIdentifier" />
              </svg>
              <i v-else-if="btn.iconIdentifier" :class="btn.iconIdentifier" class="select-btn__icon" />
              <img v-else-if="btn.iconUrl" :alt="btn.caption" :src="btn.iconUrl" class="select-btn__img">
              <span class="select-btn__caption">{{ btn.caption }}</span>
              <span v-if="btn.info" class="select-btn__info">{{ btn.info }}</span>
            </label>
          </div>
        </div>
      </div>
      <div v-if="errorText" class="">
        <p class="form__error">
          {{ errorText }}
        </p>
      </div>
    </div>
  </div>
</template>
